import React, { useEffect, useState } from 'react';
import { useUser } from '../../hooks/useUser';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import {
  FetchRun,
  FetchRunItem,
  getFetchRuns,
  getFetchRun,
  getFetchRunItems,
  deleteFetchJob,
} from '../../services/fetchJobsService';
import {
  Visibility,
  VisibilityOff,
  Delete,
  Stop,
  Refresh,
  AutorenewOutlined,
  ExpandMore,
  ExpandLess,
  PlayArrow,
  CheckCircleOutline,
  ErrorOutline,
  WarningAmber,
  Schedule,
  Storage,
  SyncAlt,
  InfoOutlined,
} from '@mui/icons-material';

const formatTriggeredBy = (value: string | null): string => {
  if (!value) return 'System';
  if (value === 'scheduler:cron') return 'Daily Cron';
  if (value.startsWith('admin:frontend:')) return 'Manual';
  return value;
};

const formatDuration = (startedAt: string | null, endedAt: string | null): string => {
  if (!startedAt || !endedAt) return '';
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const formatDateShort = (value: string | null): string => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return value;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'running': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'success': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'partial': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'failed': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'skipped': return 'bg-gray-50 text-gray-500 border-gray-200';
    case 'cancelled': return 'bg-gray-50 text-gray-500 border-gray-200';
    default: return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getItemStatusIcon = (status: string) => {
  switch (status) {
    case 'success': return <span className="text-emerald-500">&#10003;</span>;
    case 'failed': return <span className="text-rose-500">&#10007;</span>;
    case 'skipped': return <span className="text-gray-400">&#8250;&#8250;</span>;
    default: return <span className="text-gray-400">&#8226;</span>;
  }
};

export const DataFetch: React.FC = () => {
  const { data: currentUser } = useUser();
  useCardBar({ visible: false });

  const [runs, setRuns] = useState<FetchRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [watchingRunId, setWatchingRunId] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [runItems, setRunItems] = useState<Record<number, FetchRunItem[]>>({});
  const [loadingItems, setLoadingItems] = useState<number | null>(null);

  const loadRuns = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const data = await getFetchRuns();
      const sorted = data.sort((a, b) => {
        if (a.status === 'running' && b.status !== 'running') return -1;
        if (b.status === 'running' && a.status !== 'running') return 1;
        return b.id - a.id;
      });
      setRuns(sorted);
    } catch (err: any) {
      console.error('Error fetching runs', err);
      setError(err.message || 'Failed to load fetch runs');
    } finally {
      if (!silent) setLoading(false);
      else setRefreshing(false);
    }
  };

  const toggleExpand = async (runId: number) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }
    setExpandedRunId(runId);
    if (!runItems[runId]) {
      setLoadingItems(runId);
      try {
        const items = await getFetchRunItems(runId);
        setRunItems(prev => ({ ...prev, [runId]: items }));
      } catch (err) {
        console.error('Error loading run items', err);
      } finally {
        setLoadingItems(null);
      }
    }
  };

  useEffect(() => {
    if (currentUser?.id) loadRuns();
  }, [currentUser?.id]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => loadRuns(true), 30000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  useEffect(() => {
    if (!watchingRunId) return;
    const id = setInterval(async () => {
      try {
        const updated = await getFetchRun(watchingRunId);
        setRuns(prev => prev.map(r => r.id === watchingRunId ? updated : r));
        if (updated.status !== 'running') setWatchingRunId(null);
      } catch (err) {
        console.error('Error polling run', err);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [watchingRunId]);

  const handleStopJob = async (id: number) => {
    if (!confirm(`Stop job #${id}?`)) return;
    try {
      await deleteFetchJob(id);
      setRuns(prev => prev.map(r => r.id === id ? { ...r, status: 'cancelled', endedAt: new Date().toISOString() } : r));
    } catch (err: any) {
      setError(err.message || 'Failed to stop job');
    }
  };

  const handleDeleteJob = async (id: number) => {
    if (!confirm(`Delete job #${id}?`)) return;
    try {
      await deleteFetchJob(id);
      setRuns(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete job');
    }
  };

  // Derive human-readable status
  const latestRun = runs.length > 0 ? runs[0] : null;
  const lastOkRun = runs.find(r => r.status === 'success' || r.status === 'partial');

  const formatTimeOfDay = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const getRelativeDay = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'today';
    if (new Date(now.getTime() - 86400000).toDateString() === d.toDateString()) return 'yesterday';
    return `on ${d.toLocaleDateString()}`;
  };

  type HealthBanner = {
    icon: React.ReactNode;
    title: string;
    bg: string;
    border: string;
    titleColor: string;
    badges: { label: string; color: string; bg: string }[];
    subtitle?: string;
  };

  const getHealthBanner = (): HealthBanner => {
    if (!latestRun) {
      return {
        icon: <InfoOutlined sx={{ fontSize: 22 }} className="text-gray-400" />,
        title: 'No fetch runs recorded yet',
        titleColor: 'text-gray-600',
        bg: 'bg-gray-50', border: 'border-gray-200',
        badges: [],
        subtitle: 'The fetcher runs daily at 06:00 UTC from the VM.',
      };
    }

    if (latestRun.status === 'running') {
      const pct = latestRun.totalQuoteSources && latestRun.processedQuoteSources
        ? Math.round((latestRun.processedQuoteSources / latestRun.totalQuoteSources) * 100)
        : 0;
      return {
        icon: <SyncAlt sx={{ fontSize: 22 }} className="text-blue-500 animate-spin" />,
        title: 'Fetch is running now',
        titleColor: 'text-blue-700',
        bg: 'bg-blue-50', border: 'border-blue-200',
        badges: [
          { label: `${pct}% complete`, color: 'text-blue-700', bg: 'bg-blue-100' },
          { label: `${latestRun.processedQuoteSources || 0} / ${latestRun.totalQuoteSources || 0} sources`, color: 'text-blue-600', bg: 'bg-blue-100' },
        ],
      };
    }

    const day = getRelativeDay(latestRun.startedAt);
    const time = formatTimeOfDay(latestRun.startedAt);
    const success = latestRun.successCount || 0;
    const skipped = latestRun.skippedCount || 0;
    const failed = latestRun.failedCount || 0;
    const total = latestRun.totalQuoteSources || 0;
    const points = latestRun.totalPricePointsSaved || 0;

    if (latestRun.status === 'success') {
      const allSkipped = skipped === total;
      const badges: HealthBanner['badges'] = [
        { label: `${day} at ${time}`, color: 'text-gray-600', bg: 'bg-white' },
      ];
      if (success > 0) badges.push({ label: `${success} fetched`, color: 'text-emerald-700', bg: 'bg-emerald-100' });
      if (skipped > 0) badges.push({ label: `${skipped} up to date`, color: 'text-gray-500', bg: 'bg-gray-100' });
      if (points > 0) badges.push({ label: `${points.toLocaleString()} pts saved`, color: 'text-blue-700', bg: 'bg-blue-100' });
      return {
        icon: <CheckCircleOutline sx={{ fontSize: 22 }} className="text-emerald-500" />,
        title: allSkipped ? 'All sources are up to date' : 'Last fetch completed successfully',
        titleColor: 'text-emerald-700',
        bg: 'bg-emerald-50', border: 'border-emerald-200',
        badges,
        subtitle: allSkipped ? `All ${total} sources already had the latest data.` : undefined,
      };
    }

    if (latestRun.status === 'partial') {
      return {
        icon: <WarningAmber sx={{ fontSize: 22 }} className="text-amber-500" />,
        title: 'Last fetch completed with issues',
        titleColor: 'text-amber-700',
        bg: 'bg-amber-50', border: 'border-amber-200',
        badges: [
          { label: `${day} at ${time}`, color: 'text-gray-600', bg: 'bg-white' },
          { label: `${success} ok`, color: 'text-emerald-700', bg: 'bg-emerald-100' },
          { label: `${failed} failed`, color: 'text-rose-700', bg: 'bg-rose-100' },
          { label: `${skipped} skipped`, color: 'text-gray-500', bg: 'bg-gray-100' },
        ],
      };
    }

    if (latestRun.status === 'failed') {
      return {
        icon: <ErrorOutline sx={{ fontSize: 22 }} className="text-rose-500" />,
        title: 'Last fetch failed',
        titleColor: 'text-rose-700',
        bg: 'bg-rose-50', border: 'border-rose-200',
        badges: [
          { label: `${day} at ${time}`, color: 'text-gray-600', bg: 'bg-white' },
          ...(failed > 0 ? [{ label: `${failed} of ${total} failed`, color: 'text-rose-700', bg: 'bg-rose-100' }] : []),
        ],
        subtitle: latestRun.notes || undefined,
      };
    }

    return {
      icon: <InfoOutlined sx={{ fontSize: 22 }} className="text-gray-400" />,
      title: `Last run: ${latestRun.status}`,
      titleColor: 'text-gray-600',
      bg: 'bg-gray-50', border: 'border-gray-200',
      badges: [{ label: `${day} at ${time}`, color: 'text-gray-600', bg: 'bg-white' }],
    };
  };

  const health = getHealthBanner();

  return (
    <div className="px-4 py-3 flex flex-col" style={{ height: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-3 flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-900 text-left">Market Data Fetch</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadRuns(true)}
            disabled={refreshing}
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <Refresh className={refreshing ? 'animate-spin' : ''} sx={{ fontSize: 20 }} />
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`p-1.5 rounded transition-colors ${
              autoRefresh ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-100'
            }`}
            title={autoRefresh ? 'Auto-refresh ON (30s)' : 'Auto-refresh OFF'}
          >
            <AutorenewOutlined sx={{ fontSize: 20 }} />
          </button>
          <button
            disabled
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-gray-300 text-gray-500 cursor-not-allowed"
            title="Coming soon — run daily-fetch.bat on the VM to trigger manually"
          >
            <PlayArrow sx={{ fontSize: 16 }} />
            Trigger Fetch
          </button>
        </div>
      </div>

      {/* Human-readable status banner */}
      {!loading && (
        <div className={`flex items-center gap-2.5 mb-3 px-4 py-2.5 rounded-lg border flex-shrink-0 ${health.bg} ${health.border}`}>
          <span className="flex-shrink-0">{health.icon}</span>
          <p className={`text-sm font-medium ${health.titleColor}`}>{health.title}</p>
          {health.badges.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {health.badges.map((badge, i) => (
                <React.Fragment key={i}>
                  <span className="text-gray-300 text-[8px]">&#9679;</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.color} ${badge.bg}`}>
                    {badge.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          {runs.length > 1 && lastOkRun && lastOkRun !== latestRun && (
            <span className="text-[10px] text-gray-400 ml-1">
              (prev success: #{lastOkRun.id})
            </span>
          )}
          <span className="ml-auto text-[10px] text-gray-400 flex-shrink-0">{runs.length} runs</span>
        </div>
      )}

      {error && (
        <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-xs flex-shrink-0">{error}</div>
      )}

      {/* Scrollable table */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-500 text-xs">Loading...</div>
      ) : runs.length === 0 ? (
        <div className="text-gray-500 text-xs">No fetch runs yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg flex-1 min-h-0 overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 w-16">Run</th>
                <th className="px-3 py-2 w-24">Status</th>
                <th className="px-3 py-2 w-20 text-center">Sources</th>
                <th className="px-3 py-2">Results</th>
                <th className="px-3 py-2 w-16 text-right">Points</th>
                <th className="px-3 py-2 w-20 text-right">Duration</th>
                <th className="px-3 py-2 w-24">Trigger</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map((run) => {
                const isWatching = watchingRunId === run.id;
                const isExpanded = expandedRunId === run.id;
                const total = run.totalQuoteSources || 0;
                const success = run.successCount || 0;
                const failed = run.failedCount || 0;
                const skipped = run.skippedCount || 0;
                const processed = run.processedQuoteSources || 0;
                const duration = formatDuration(run.startedAt, run.endedAt);
                const isTerminal = ['success', 'partial', 'failed', 'cancelled'].includes(run.status);
                const items = runItems[run.id];

                // Stacked bar widths
                const successPct = total > 0 ? (success / total) * 100 : 0;
                const failedPct = total > 0 ? (failed / total) * 100 : 0;
                const skippedPct = total > 0 ? (skipped / total) * 100 : 0;
                const progressPct = total > 0 ? (processed / total) * 100 : 0;

                return (
                  <React.Fragment key={run.id}>
                    <tr
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${run.status === 'cancelled' ? 'opacity-40' : ''} ${isExpanded ? 'bg-blue-50/30' : ''}`}
                      onClick={() => isTerminal && total > 0 && toggleExpand(run.id)}
                    >
                      {/* Expand arrow */}
                      <td className="px-1 py-2 text-center">
                        {isTerminal && total > 0 ? (
                          <span className="text-gray-400">
                            {isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                          </span>
                        ) : null}
                      </td>

                      {/* Run ID */}
                      <td className="px-3 py-2">
                        <span className="text-xs font-semibold text-gray-700">#{run.id}</span>
                      </td>

                      {/* Status Badge */}
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${getStatusColor(run.status)}`}>
                          {run.status === 'running' && <span className="mr-1 inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />}
                          <span className="capitalize">{run.status}</span>
                        </span>
                      </td>

                      {/* Sources count */}
                      <td className="px-3 py-2 text-center">
                        <span className="text-xs text-gray-600 font-mono">
                          {run.status === 'running' ? `${processed}/${total}` : total || '—'}
                        </span>
                      </td>

                      {/* Results bar + labeled counts */}
                      <td className="px-3 py-2">
                        {total > 0 ? (
                          <div className="flex items-center gap-2">
                            {/* Stacked bar */}
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden flex">
                              {run.status === 'running' ? (
                                <div
                                  className="h-full bg-blue-400 transition-all duration-500"
                                  style={{ width: `${progressPct}%` }}
                                />
                              ) : (
                                <>
                                  {successPct > 0 && (
                                    <div className="h-full bg-emerald-400" style={{ width: `${successPct}%` }} />
                                  )}
                                  {skippedPct > 0 && (
                                    <div className="h-full bg-gray-300" style={{ width: `${skippedPct}%` }} />
                                  )}
                                  {failedPct > 0 && (
                                    <div className="h-full bg-rose-400" style={{ width: `${failedPct}%` }} />
                                  )}
                                </>
                              )}
                            </div>
                            {/* Labeled counts */}
                            <div className="flex items-center gap-2.5 text-[10px] min-w-[150px]">
                              {run.status === 'running' ? (
                                <span className="text-blue-600 font-medium">{Math.round(progressPct)}%</span>
                              ) : (
                                <>
                                  {success > 0 && (
                                    <span className="text-emerald-600" title="New data fetched and saved">
                                      <span className="font-semibold">{success}</span> fetched
                                    </span>
                                  )}
                                  {skipped > 0 && (
                                    <span className="text-gray-400" title="Already up-to-date, no new data needed">
                                      <span className="font-semibold">{skipped}</span> up to date
                                    </span>
                                  )}
                                  {failed > 0 && (
                                    <span className="text-rose-500 font-medium" title="Fetch failed — click row for details">
                                      <span className="font-semibold">{failed}</span> failed
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Price points */}
                      <td className="px-3 py-2 text-right">
                        {run.totalPricePointsSaved ? (
                          <span className="text-xs font-semibold text-blue-600">{run.totalPricePointsSaved.toLocaleString()}</span>
                        ) : isTerminal ? (
                          <span className="text-xs text-gray-300">0</span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Duration */}
                      <td className="px-3 py-2 text-right">
                        {duration ? (
                          <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-600 rounded-full border border-blue-200">
                            {duration}
                          </span>
                        ) : run.status === 'running' ? (
                          <span className="text-[10px] text-blue-500 animate-pulse">...</span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Trigger */}
                      <td className="px-3 py-2">
                        <span className="text-[10px] text-gray-500">{formatTriggeredBy(run.triggeredBy)}</span>
                      </td>

                      {/* Started at */}
                      <td className="px-3 py-2">
                        <span className="text-xs text-gray-500">{formatDateShort(run.startedAt)}</span>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                        {run.status === 'running' ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setWatchingRunId(isWatching ? null : run.id)}
                              className={`p-0.5 rounded ${isWatching ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                              title={isWatching ? 'Stop watching' : 'Watch live'}
                            >
                              {isWatching ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
                            </button>
                            <button
                              onClick={() => handleStopJob(run.id)}
                              className="p-0.5 rounded text-orange-400 hover:text-orange-600"
                              title="Stop"
                            >
                              <Stop sx={{ fontSize: 14 }} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDeleteJob(run.id)}
                            className="p-0.5 rounded text-gray-300 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* Expanded detail rows */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} className="px-0 py-0">
                          <div className="bg-gray-50 border-t border-b border-gray-200">
                            {/* Notes */}
                            {run.notes && (
                              <div className="px-6 py-2 text-[11px] text-gray-600 bg-gray-100 border-b border-gray-200">
                                <span className="font-medium text-gray-500 mr-1">Summary:</span>
                                {run.notes}
                              </div>
                            )}
                            {loadingItems === run.id ? (
                              <div className="px-6 py-3 text-xs text-gray-400">Loading details...</div>
                            ) : items && items.length > 0 ? (
                              <table className="w-full">
                                <thead>
                                  <tr className="text-[9px] text-gray-400 uppercase tracking-wider">
                                    <th className="px-6 py-1.5 text-left w-8"></th>
                                    <th className="px-2 py-1.5 text-left w-32">Symbol</th>
                                    <th className="px-2 py-1.5 text-left w-16">Status</th>
                                    <th className="px-2 py-1.5 text-right w-14">Points</th>
                                    <th className="px-2 py-1.5 text-left w-40">Window</th>
                                    <th className="px-2 py-1.5 text-left">Note</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((item) => (
                                    <tr key={item.id} className="border-t border-gray-100 hover:bg-white/50">
                                      <td className="px-6 py-1 text-center text-xs">
                                        {getItemStatusIcon(item.status)}
                                      </td>
                                      <td className="px-2 py-1">
                                        <span className="text-[11px] font-mono font-medium text-gray-700">{item.sourceSymbol}</span>
                                      </td>
                                      <td className="px-2 py-1">
                                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium border ${getStatusColor(item.status)}`}>
                                          {item.status}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        <span className={`text-[11px] font-mono ${item.rowsInserted > 0 ? 'text-blue-600 font-medium' : 'text-gray-300'}`}>
                                          {item.rowsInserted || '—'}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1">
                                        {item.fetchWindowStart && item.fetchWindowEnd ? (
                                          <span className="text-[10px] text-gray-400">
                                            {item.fetchWindowStart} &rarr; {item.fetchWindowEnd}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-gray-300">—</span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1">
                                        {item.errorMessage ? (
                                          <span className={`text-[11px] ${item.status === 'failed' ? 'text-rose-500' : 'text-gray-500'}`} title={item.errorMessage}>
                                            {item.errorMessage}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-gray-300">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="px-6 py-3 text-xs text-gray-400">No item details available.</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default DataFetch;
